import {
  Cancel01Icon,
  UserCircle02Icon,
  PathfinderDivideIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { RowSelectionState } from "@tanstack/react-table";
import { applicationApi } from "api/application";
import { toast } from "react-toastify";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  Modal,
  IconButtonVariant
} from "shared/components";
import { useState } from "react";
import { AssigneeSelect } from "../AssigneeSelect";

export const SelectPopUp = ({
  selectedRow,
  selectedPropertyIds,
  clearSelectedRows,
  onMergeClick
}: {
  selectedRow: RowSelectionState;
  // Property ids of the selected applications; the assignee dropdown only
  // offers users who can access all of them.
  selectedPropertyIds: number[];
  clearSelectedRows: () => void;
  onMergeClick: () => void;
}) => {
  const queryClient = useQueryClient();
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState<number | null>(
    null
  );

  const { mutateAsync: bulkAssignApplications, isPending: isAssigning } =
    useMutation({
      mutationKey: ["BULK", "ASSIGN", "APPLICATIONS"],
      onSuccess: () => {
        clearSelectedRows();
        setIsAssignModalOpen(false);
        setSelectedAssigneeId(null);
        queryClient.refetchQueries({ queryKey: ["APPLICANTS_TABLE"] });
        queryClient.refetchQueries({ queryKey: ["APPLICATION_ACTIVITY_LOG"] });
        toast.success("Applications assigned successfully");
      },
      onError: () => {
        toast.error("Failed to assign applications");
      },
      mutationFn: async ({
        application_ids,
        partner_user_id
      }: {
        application_ids: number[];
        partner_user_id: number | null;
      }) => {
        return applicationApi.bulkAssignApplications({
          application_ids,
          assignee_ids: partner_user_id !== null ? [partner_user_id] : []
        });
      }
    });

  const handleBulkAssign = () => {
    const applicationIds = Object.keys(selectedRow)
      .filter((key) => selectedRow[key])
      .map(Number);
    if (applicationIds.length === 0) return;
    bulkAssignApplications({
      application_ids: applicationIds,
      partner_user_id: selectedAssigneeId
    });
  };

  const amountOfSelectedApplications =
    Object.values(selectedRow).filter(Boolean).length;

  return Object.values(selectedRow).filter(Boolean).length ? (
    <>
      <div className="h-16 divide-x-[1px] shadow-lg bg-white px-4 py-2 rounded-lg border border-alpha-100 z-[11] fixed right-10 bottom-24 animate-appear flex justify-end items-center gap-4">
        <p className="text-base font-medium text-content-1000 truncate">
          {amountOfSelectedApplications}{" "}
          {amountOfSelectedApplications > 1 ? "applications" : "application"}{" "}
          selected
        </p>

        <Button
          size={ButtonSize.SM}
          HugeIcon={UserCircle02Icon}
          variant={ButtonVariant.TEXT}
          onClick={() => setIsAssignModalOpen(true)}
          className="flex items-center justify-center gap-2 pl-4"
        >
          Assign
        </Button>
        <Button
          size={ButtonSize.SM}
          HugeIcon={PathfinderDivideIcon}
          variant={ButtonVariant.TEXT}
          onClick={onMergeClick}
          disabled={amountOfSelectedApplications < 2}
          className="flex items-center justify-center gap-2 pl-4"
        >
          Merge application
        </Button>

        <div className="pl-4 h-5 flex flex-shrink-0 items-center">
          <IconButton
            size={IconButtonSize.SM}
            variant={IconButtonVariant.BASE}
            HugeIcon={Cancel01Icon}
            onClick={clearSelectedRows}
          />
        </div>
      </div>

      <Modal
        opened={isAssignModalOpen}
        onClose={() => {
          setIsAssignModalOpen(false);
          setSelectedAssigneeId(null);
        }}
        panelClassName="w-full max-w-[400px] p-6"
      >
        <div className="flex flex-col gap-4">
          <h3 className="text-xl leading-6 font-bold text-content-1000">
            Assign applications
          </h3>
          <div className="flex flex-col gap-2">
            <AssigneeSelect
              value={selectedAssigneeId}
              propertyIds={selectedPropertyIds}
              onChange={(partner_user_id) => {
                setSelectedAssigneeId(partner_user_id);
              }}
              label="Assign to"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              size={ButtonSize.SM}
              variant={ButtonVariant.BASE}
              onClick={() => {
                setIsAssignModalOpen(false);
                setSelectedAssigneeId(null);
              }}
            >
              Cancel
            </Button>
            <Button
              size={ButtonSize.SM}
              variant={ButtonVariant.PRIMARY}
              loading={isAssigning}
              onClick={handleBulkAssign}
            >
              Assign
            </Button>
          </div>
        </div>
      </Modal>
    </>
  ) : null;
};
